import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "../utils/firebase";
import { trackLogin } from "../features/dashai/utils/trackLogin";
import { track } from "@/analytics";
import {
  fetchUserData,
  fetchUserCountries,
  CreateGoogleUserDoc,
  forgotPasswordSendEmail,
  forgotPasswordResetPassword,
  changeOwnPassword,
} from "../api/marketOverviewApi";
import {
  startImpersonationApi,
  stopImpersonationApi,
  restoreImpersonationApi,
} from "../api/adminApi";
import { clearAuthCache } from "../utils/authUtils";
import { clearActivityTimestamp } from "../hooks/useIdleTimer";
import { setCurrentCountry } from "./filtersSlice";
import { fetchWorkspaceConfig } from "./workspaceConfigSlice";
import logger from "../utils/logger";
import { extractClientId } from "../utils/extractClientId";

const googleProvider = new GoogleAuthProvider();

// The Dashmote staff email domain. Single source of truth for the literal so
// every domain check anchors on the same suffix.
export const DASHMOTE_EMAIL_DOMAIN = "@dashmote.com";

// Whether an email belongs to the Dashmote staff domain (case-insensitive).
// Domain-only — does NOT exclude demo accounts; use `isDashmoteStaffUser` when
// the demo exclusion is also required.
export const isDashmoteStaffEmail = (email) =>
  typeof email === "string" &&
  email.toLowerCase().endsWith(DASHMOTE_EMAIL_DOMAIN);

// Whether the user is Dashmote staff with implicit all-workspace access.
// Mirrors the backend predicate `is_dashmote_staff`: a @dashmote.com address
// whose server-controlled `account_type` is unset or blank. Any classification,
// such as "demo" or "test", keeps the account scoped.
export const isDashmoteStaffUser = (userData) => {
  const accountType = userData?.account_type;
  return (
    isDashmoteStaffEmail(userData?.email) &&
    (accountType == null ||
      (typeof accountType === "string" && accountType.trim() === ""))
  );
};

// The workspace the session is operating on, which is what anything scoping to
// what the user is looking at should read. The switcher owns it, so it moves
// the moment they switch. Before they switch there is nothing to read, and the
// workspace they land on is their stored default — a preference that does not
// follow the switcher and is a different question from this one.
export const selectActiveWorkspaceKey = (state) =>
  state.auth?.selectedWorkspace?.key || state.auth?.userData?.default_id_client || "";

// Whether the user may override an invite-domain-allowlist block.
// Mirrors the backend's override authority (`is_dashmote_admin`): the
// user-level `dashmote_admin` capability (the /admin gate), which the server
// requires together with a @dashmote.com address. A non-admin's override is
// ignored server-side regardless, so this only governs whether the affordance
// is *shown* — gating on email alone would surface it to @dashmote.com users
// without the flag, whose override the backend silently refuses.
export const canOverrideInviteBlock = (userData) =>
  isDashmoteStaffEmail(userData?.email) && userData?.dashmote_admin === true;

// For a staff user viewing a workspace they aren't a member of, synthesize a
// client-side `super_admin` entry in their id_client map so every consumer of
// `userData.id_client[selectedKey]` (role / product_scopes) resolves — matching
// the backend, which grants staff the full geoscope for any workspace.
// Client-side only, never persisted; a no-op for non-staff, missing keys, or
// entries that already exist. Safe to call inside reducers — Immer drafts the
// mutation.
const ensureImplicitWorkspaceEntry = (userData, workspaceKey) => {
  if (!workspaceKey || !isDashmoteStaffUser(userData)) return;
  if (!userData.id_client || typeof userData.id_client !== "object") return;
  if (userData.id_client[workspaceKey]) return;
  userData.id_client[workspaceKey] = {
    role: "super_admin",
    is_super_admin: true,
    product_scopes: null,
  };
};

// A switcher entry for a workspace key: the key plus its display label.
export const toSwitcherEntry = (key) => ({
  key,
  value: key.toUpperCase().replace(/_/g, " "),
});

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  isInitialized: false,
  id_client: null,
  id_user: null,
  userData: null, // Store complete user data from API
  userDataFetchedThisSession: false, // Track if userData was fetched in current session (not persisted)
  userDataFetchError: null, // Error from fetchUserDataAction (not persisted)
  countries: [],
  // The API's default country for the last fetched countries list.
  defaultCountry: null,
  // The countries fetch in flight as
  // { key: "<uid>:<id_client>:<product_name>", requestId }, so a second caller
  // asking for the same list skips its fetch.
  countriesRequest: null,
  // The switcher list, exactly as `/users` returns it in `available_workspaces`.
  availableWorkspaces: [],
  // True when `/users` could not resolve the list, so the switcher shows an
  // error with a retry rather than an empty list.
  workspaceListError: false,
  selectedWorkspace: null, // Currently selected workspace
  // Filter-context lifecycle. The workspace switch commits id_client and
  // clamps country atomically; data-fetch effects gate on status === "ready" so a
  // mid-transition (new workspace, old country) pair can never reach the wire.
  // "switching" spans a workspace change until its fresh countries resolve;
  // "resolving_scope" spans a load, or a cleared country list, until a
  // countries fetch settles.
  status: "resolving_scope", // "resolving_scope" | "switching" | "ready"
  // Impersonation state — true token-based impersonation.
  // Firebase identity is swapped via signInWithCustomToken, so all API calls
  // (FastAPI + DashAI Firebase functions) authenticate as the target user.
  isImpersonating: false,
  impersonatedUserEmail: null,
  impersonationSessionId: null,
  impersonationExpiresAt: null,
};

// Email + password sign-in
export const loginWithEmail = createAsyncThunk(
  "auth/loginWithEmail",
  async ({ email, password, message }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      if (message) {
        message.success({
          key: "auth",
          content: "Login Successfully!",
          duration: 3,
        });
      }
      // Track login event via the shared trackLogin util;
      // no-ops if VITE_FIREBASE_FUNCTIONS_URL is unset.
      try {
        const idToken = await userCredential.user.getIdToken();
        trackLogin({
          eventType: "login",
          authMethod: "email",
          idToken,
          userId: userCredential.user.uid,
        });
      } catch (e) { console.error("[trackLogin] Failed:", e); }

      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
      };
    } catch (error) {
      logger.error("[loginWithEmail] Login failed:", error);
      if (message) {
        message.error({
          key: "auth",
          content: "Login failed! Please check your credentials.",
          duration: 3,
        });
      }
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserDataAction = createAsyncThunk(
  "auth/fetchUserData",
  async ({ withScope = true }, { rejectWithValue, getState }) => {
    try {
      const { auth: authState } = getState();

      if (!authState.isAuthenticated || !authState.user) {
        throw new Error("User not authenticated");
      }

      const userData = await fetchUserData(withScope);

      return userData;
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      return rejectWithValue(error.message, { httpStatus: error.status });
    }
  }
);

// Keyed on the acting Firebase uid as well as the workspace, because
// impersonation swaps the identity while the workspace can stay the same.
const countriesRequestKey = (auth, productName) =>
  `${auth.user?.uid}:${auth.id_client}:${productName ?? "execution_view"}`;

// Fetches the countries the user may open and the API's default among them.
// `targetCountry` is a country the caller wants selected when the list
// contains it; filtersSlice applies it together with the default. A call is
// skipped while the same identity's list for the same workspace and product is
// in flight, unless `force` is set: a workspace switch awaits its own fetch
// and must not lose its target to a page's.
export const fetchClientCountries = createAsyncThunk(
  "auth/fetchClientCountries",
  /** @param {{ id_user?: string; product_name?: string; targetCountry?: string | null; force?: boolean }} payload */
  async (payload = {}, { getState, rejectWithValue }) => {
    const { auth } = getState();
    const { id_client, id_user: stateIdUser, userData, user } = auth;

    if (!id_client) {
      return rejectWithValue("No client ID available.");
    }

    const resolvedIdUser =
      payload.id_user ?? stateIdUser ?? userData?.id_user ?? user?.uid ?? null;

    if (!resolvedIdUser) {
      return rejectWithValue("No user ID available.");
    }

    const resolvedProductName = payload.product_name ?? "execution_view";

    try {
      const response = await fetchUserCountries({
        id_client,
        id_user: resolvedIdUser,
        product_name: resolvedProductName,
      });
      // A list fetched for a workspace or identity the session has since left
      // must not replace the current list or select one of its countries.
      const now = getState().auth;
      if (now.id_client !== id_client || now.user?.uid !== user?.uid) {
        return rejectWithValue(
          "Workspace or identity changed during countries fetch"
        );
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch client countries"
      );
    }
  },
  {
    condition: (payload = {}, { getState }) => {
      if (payload.force) return true;
      const { auth } = getState();
      return (
        auth.countriesRequest?.key !==
        countriesRequestKey(auth, payload.product_name)
      );
    },
  }
);

// Combined thunk: switch workspace and update country
export const switchWorkspaceAndUpdateCountry = createAsyncThunk(
  "auth/switchWorkspaceAndUpdateCountry",
  /** @param {{ workspace: any; product_name?: string; targetCountry?: string | null }} arg */
  async (
    { workspace, product_name = "execution_view", targetCountry = null },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Emit workspace_switched to the governed catalog. to_workspace is
      // the target's key; from_workspace is omitted because the .pending reducer
      // has already set state.selectedWorkspace to the new workspace by the time
      // this body runs, so the previous key can't be read purely here.
      if (workspace?.key) track("workspace_switched", { to_workspace: workspace.key });

      // Step 0: Clear the old country immediately to prevent components from using stale country
      dispatch(setCurrentCountry(null));

      // Step 1: Workspace is already set in the pending reducer (runs synchronously)

      // Step 2: Fetch the new workspace's countries and config together, so
      // the switch settles only once both have arrived. The countries'
      // fulfilled action selects the country in the same dispatch: the
      // requested target (e.g. the refresh-notification email deep-link) when
      // the workspace offers it, else the API's default. A config failure is
      // not fatal: the slice records it and the dashboard uses the default
      // layout.
      const [countriesResult] = await Promise.all([
        dispatch(fetchClientCountries({ product_name, targetCountry, force: true })),
        workspace?.key
          ? dispatch(fetchWorkspaceConfig({ idClient: workspace.key }))
          : null,
      ]);

      if (fetchClientCountries.rejected.match(countriesResult)) {
        return rejectWithValue(
          countriesResult.payload || "Failed to fetch countries"
        );
      }

      return { workspace };
    } catch (error) {
      console.error("[switchWorkspaceAndUpdateCountry] Error:", error);
      return rejectWithValue(
        error.message || "Failed to switch workspace and update country"
      );
    }
  },
  {
    // Every entry point (switcher, deep link, email link) switches only to a
    // workspace the server listed. With no list known yet, or the list
    // unresolved, the key cannot be checked and the switch goes ahead; the
    // server still authorizes every request. Re-picking the selected workspace
    // is a no-op, because a switch clears the country and the table's filters;
    // an email link naming a country still switches, to select that country.
    condition: ({ workspace, targetCountry }, { getState }) => {
      if (!workspace?.key) return false;
      const { availableWorkspaces, workspaceListError, selectedWorkspace } = getState().auth;
      if (workspace.key === selectedWorkspace?.key && !targetCountry) return false;
      if (workspaceListError || !availableWorkspaces?.length) return true;
      return availableWorkspaces.some((ws) => ws.key === workspace.key);
    },
  }
);


// True impersonation: sign in as the target user with a custom Firebase token.
// The backend mints the token, creates a session, and logs the audit event.
// All subsequent API calls (FastAPI + DashAI Firebase functions) authenticate as the target user.
export const impersonateUser = createAsyncThunk(
  "auth/impersonateUser",
  /** @param {{ email: string }} arg */
  async ({ email }, { rejectWithValue }) => {
    try {
      // 1. Call backend — creates session, mints tokens, writes audit log
      const result = await startImpersonationApi({ target_email: email });

      // 2. Sign in as the target user using the custom Firebase token
      await signInWithCustomToken(auth, result.target_token);
      // Drop the cached admin ID token so subsequent API calls authenticate as
      // the target. getAuthToken() caches the token for 60min and is NOT
      // invalidated by the Firebase identity swap on its own — without this,
      // makeAuthenticatedRequest keeps sending the admin token captured by
      // startImpersonationApi, so the impersonation session, read-only
      // enforcement, and audit trail never engage (and exit can't restore).
      clearAuthCache();

      return {
        sessionId: result.session_id,
        targetEmail: result.target_email,
        targetUid: result.target_uid,
        expiresAt: result.expires_at,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to impersonate user");
    }
  }
);

// Exit impersonation: restore the admin's Firebase identity, then stop the
// session. The restore token is minted on demand by the backend (authorized by
// the impersonation claims still on the current token) rather than stored
// client-side — so exit works even after a page reload mid-impersonation, where
// an in-memory token would have been lost while Firebase kept the target session.
//
// Order matters: restore admin FIRST so the stop call authenticates as admin
// (verify_dashmote_admin requires a @dashmote.com email — it fails while still
// signed in as the target user).
export const exitImpersonation = createAsyncThunk(
  "auth/exitImpersonation",
  async (_, { getState, rejectWithValue }) => {
    const { auth: authState } = getState();
    const sessionId = authState.impersonationSessionId;
    try {
      if (!sessionId) {
        throw new Error("No impersonation session to restore from");
      }

      // 1. Ask the backend to mint a fresh restore token, then swap back to the
      //    admin's own Firebase identity.
      // Drop the stale cached token FIRST so the restore call authenticates with
      // the live target token (which carries the impersonation claims the
      // endpoint authorizes against) — not a 60min-cached admin token that the
      // endpoint would reject with 403 "Not an impersonation session token".
      clearAuthCache();
      const { admin_restore_token: adminRestoreToken } =
        await restoreImpersonationApi({ session_id: sessionId });
      await signInWithCustomToken(auth, adminRestoreToken);
      // Drop the target token now that we're admin again, so the stop call (and
      // everything after) authenticates as the admin.
      clearAuthCache();

      // Capture the restored admin identity — onAuthStateChanged is unsubscribed
      // after init, so it won't update state.user on this token swap.
      const restored = auth.currentUser;
      const adminUser = restored
        ? {
            uid: restored.uid,
            email: restored.email,
            displayName: restored.displayName,
            photoURL: restored.photoURL,
          }
        : null;

      // 2. Stop the server-side session (now as admin — verify_dashmote_admin passes).
      try {
        await stopImpersonationApi({ session_id: sessionId });
      } catch (e) {
        console.error("Failed to stop impersonation session:", e);
        // Continue — admin identity is already restored; the session auto-expires via TTL.
      }

      return { adminUser };
    } catch (error) {
      // Never leave the admin stranded as the target user: if the restore failed,
      // sign out fully so they re-authenticate as themselves.
      try {
        await signOut(auth);
      } catch (e) {
        console.error("Failed to sign out after failed restore:", e);
      }
      return rejectWithValue(error.message || "Failed to exit impersonation");
    }
  }
);

// Google sign-in
export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async ({ message }, { rejectWithValue }) => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const email = user.email;
      if (!isDashmoteStaffEmail(email)) {
        await signOut(auth); // sign out the disallowed account
        message.error({
          key: "auth",
          content: "Login failed! Please use dashmote account!",
          duration: 3,
        });
        return rejectWithValue("Only @dashmote.com emails are allowed.");
      }
      await CreateGoogleUserDoc({
        email: user.email,
        id_user: user.uid,
      });
      message.success({
        key: "auth",
        content: "Login Successfully!",
        duration: 3,
      });

      // Track login event via the shared trackLogin util;
      // no-ops if VITE_FIREBASE_FUNCTIONS_URL is unset.
      try {
        const idToken = await result.user.getIdToken();
        trackLogin({
          eventType: "login",
          authMethod: "google",
          idToken,
          userId: result.user.uid,
        });
      } catch (e) { console.error("[trackLogin] Failed:", e); }

      return {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

// Forgot Password - Send Reset Email
export const sendForgotPasswordEmail = createAsyncThunk(
  "auth/sendForgotPasswordEmail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordSendEmail({ email });
      return { email, response };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to send password reset email"
      );
    }
  }
);

// Reset Password - Update password with token
export const resetPasswordWithToken = createAsyncThunk(
  "auth/resetPasswordWithToken",
  async ({ email, password, token }, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordResetPassword({
        email,
        password,
        token,
      });
      return { email, response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to reset password");
    }
  }
);

// Change own password (authenticated) — used by the force-password-change
// screen after an admin reset. The backend clears the force_password_change
// flag; the caller re-fetches user data before navigating back into the app.
export const changeOwnPasswordAction = createAsyncThunk(
  "auth/changeOwnPassword",
  async ({ newPassword }, { rejectWithValue }) => {
    try {
      const response = await changeOwnPassword({ newPassword });
      // The backend sets the new password via the Firebase Admin SDK, which
      // invalidates the current session's refresh token. Without re-auth, the
      // very next token refresh fails with auth/user-token-expired and the app
      // logs the user out mid-flow. Re-authenticate with the new password to
      // get a fresh session, and clear the cached (now-stale) token so the
      // follow-up profile fetch uses the new one.
      const email = auth.currentUser?.email;
      if (email) {
        await signInWithEmailAndPassword(auth, email, newPassword);
        clearAuthCache();
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to change password");
    }
  }
);

// Sign-out
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      // Track logout event via the shared trackLogin util;
      // no-ops if VITE_FIREBASE_FUNCTIONS_URL is unset.
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const uid = auth.currentUser?.uid;
        if (idToken && uid) {
          trackLogin({ eventType: "logout", idToken, userId: uid });
        }
      } catch {
        // Silent fail
      }

      await signOut(auth);
      clearAuthCache();
      clearActivityTimestamp();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Subscribes to Firebase auth state changes
export const initializeAuth = createAsyncThunk(
  "auth/initializeAuth",
  async (_, { dispatch }) => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          };
          dispatch(authSlice.actions.setUser(userData));
          resolve(userData);
        } else {
          dispatch(authSlice.actions.setUser(null));
          resolve(null);
        }
        dispatch(authSlice.actions.setInitialized(true));
        unsubscribe();
      });
    });
  }
);

// Shared by every sign-in thunk's `fulfilled` case. A sign-in is a new
// identity in this tab, so the profile is fetched again and an earlier fetch
// failure does not outlive the session that produced it.
const applySignIn = (state, action) => {
  state.loading = false;
  state.user = action.payload;
  state.isAuthenticated = true;
  state.error = null;
  state.userDataFetchedThisSession = false;
  state.userDataFetchError = null;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setInitialized: (state, action) => {
      state.isInitialized = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Marks the cached user data stale so the next check re-fetches it
    forceRefreshUserData: (state) => {
      if (state.user) {
        state.user.dataFetched = false;
      }
    },
    // Set impersonation flags after signInWithCustomToken succeeds.
    // Firebase identity is already swapped — ProtectedRoute will re-fetch
    // the target user's data via onAuthStateChanged.
    setImpersonationActive: (state, action) => {
      const { sessionId, targetEmail, targetUid, expiresAt } = action.payload;
      state.isImpersonating = true;
      state.impersonatedUserEmail = targetEmail;
      state.impersonationSessionId = sessionId;
      state.impersonationExpiresAt = expiresAt;
      // Update state.user to reflect the target user's identity.
      // onAuthStateChanged is unsubscribed after init, so it won't fire on
      // signInWithCustomToken. We update manually to keep Redux in sync.
      // The display name and photo are the admin's and have no target
      // counterpart on a custom-token sign-in, so they are dropped rather than
      // carried: the adapter reads `displayName` ahead of the target's document
      // name, and the top bar renders `photoURL`, so either would put the
      // admin's identity on a session that must present the target.
      if (state.user) {
        state.user = {
          ...state.user,
          uid: targetUid,
          email: targetEmail,
          displayName: null,
          photoURL: null,
        };
      }
      // Reset user data so ProtectedRoute re-fetches for the new Firebase identity
      state.userDataFetchedThisSession = false;
      state.userData = null;
      state.id_client = null;
      state.availableWorkspaces = [];
      state.workspaceListError = false;
      state.selectedWorkspace = null;
      // Drop the impersonator's country list so it re-fetches for the target.
      state.countries = [];
      state.defaultCountry = null;
      state.countriesRequest = null;
      state.status = "resolving_scope";
    },
    // Clear impersonation flags after restoring admin identity.
    clearImpersonation: (state) => {
      state.isImpersonating = false;
      state.impersonatedUserEmail = null;
      state.impersonationSessionId = null;
      state.impersonationExpiresAt = null;
      // Reset user data so ProtectedRoute re-fetches for the admin's Firebase identity
      state.userDataFetchedThisSession = false;
      state.userData = null;
      state.id_client = null;
      state.availableWorkspaces = [];
      state.workspaceListError = false;
      state.selectedWorkspace = null;
      // Drop the target's country list so it re-fetches for the restored admin.
      state.countries = [];
      state.defaultCountry = null;
      state.countriesRequest = null;
      state.status = "resolving_scope";
    },
  },
  extraReducers: (builder) => {
    builder
      // Email sign-in
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, applySignIn)
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Google sign-in
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, applySignIn)
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Sign-out
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.id_client = null;
        state.id_user = null;
        state.userData = null;
        state.userDataFetchedThisSession = false;
        state.userDataFetchError = null;
        state.availableWorkspaces = [];
        state.workspaceListError = false;
        state.selectedWorkspace = null;
        // Clear impersonation state
        state.isImpersonating = false;
        state.impersonatedUserEmail = null;
        state.impersonationSessionId = null;
        state.impersonationExpiresAt = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Auth initialization
      .addCase(initializeAuth.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchUserDataAction.pending, (state, action) => {
        // A background revalidation (live-tab focus/visibility resume, or 403
        // recovery) must not flip the global loading flag — that would flash
        // ProtectedRoute's full-page spinner on every tab resume. Only a
        // foreground fetch (login / bootstrap) drives the spinner.
        if (!action.meta.arg?.background) {
          state.loading = true;
        }
        state.error = null;
        state.userDataFetchError = null;
      })
      .addCase(fetchUserDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.userDataFetchError = null;
        state.userDataFetchedThisSession = true; // Set session-specific flag (not persisted)
        state.id_user = action.payload.id_user;

        // The server owns the switcher list and the landing pick. A saved
        // selection survives only while it is still in the list, so a revoked
        // or retired workspace drops out of the session too.
        const availableKeys = action.payload.available_workspaces;
        const savedKey = state.selectedWorkspace?.key;
        let nextKey;
        if (Array.isArray(availableKeys)) {
          state.workspaceListError = false;
          state.availableWorkspaces = availableKeys.map(toSwitcherEntry);
          nextKey = availableKeys.includes(savedKey)
            ? savedKey
            : action.payload.landing_workspace || null;
        } else {
          // The server could not resolve the list. Keep whatever list the
          // session already had and let the switcher offer a retry. The
          // session stays on its saved workspace or the stored default when
          // it may reach it: staff reach any workspace, everyone else only a
          // grant, falling back to their first grant.
          state.workspaceListError = true;
          const grants =
            action.payload.id_client && typeof action.payload.id_client === "object"
              ? Object.keys(action.payload.id_client).sort()
              : [];
          const reachable = (key) =>
            Boolean(key) && (isDashmoteStaffUser(action.payload) || grants.includes(key));
          nextKey =
            [savedKey, action.payload.default_id_client].find(reachable) ||
            grants[0] ||
            null;
        }
        state.selectedWorkspace = nextKey ? toSwitcherEntry(nextKey) : null;

        // A grant-less staffer's selection needs a synthesized grant entry so
        // id_client and the per-workspace consumers resolve it. It goes on a
        // copy of the grant map, so the action payload is never mutated.
        const userData =
          action.payload.id_client && typeof action.payload.id_client === "object"
            ? { ...action.payload, id_client: { ...action.payload.id_client } }
            : action.payload;
        ensureImplicitWorkspaceEntry(userData, nextKey);
        state.userData = userData; // Store complete user data

        // Extract id_client based on selected workspace. With nothing selected
        // the session scopes to no workspace, rather than falling back to a
        // grant the server left out of the list.
        state.id_client = nextKey
          ? extractClientId(userData, state.selectedWorkspace)
          : null;
        state.error = null;
      })
      .addCase(fetchUserDataAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const httpStatus = action.meta.httpStatus;
        const transientFailure =
          httpStatus == null || httpStatus >= 500 || httpStatus === 408 || httpStatus === 429;
        const canRetryWorkspaceList =
          action.meta.arg?.workspaceListRetry === true &&
          action.meta.arg?.background === true &&
          state.isAuthenticated &&
          state.user &&
          state.userDataFetchedThisSession === true &&
          state.userData;
        if (canRetryWorkspaceList && transientFailure) {
          state.workspaceListError = true;
          state.userDataFetchError = null;
        } else {
          state.userDataFetchError = action.payload || "Failed to fetch user data";
        }
      })
      .addCase(fetchClientCountries.pending, (state, action) => {
        state.loading = true;
        state.countriesRequest = {
          key: countriesRequestKey(state, action.meta.arg?.product_name),
          requestId: action.meta.requestId,
        };
      })
      .addCase(fetchClientCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload.regions;
        state.defaultCountry = action.payload.default_country;
        if (state.countriesRequest?.requestId === action.meta.requestId) {
          state.countriesRequest = null;
        }
        if (state.status === "resolving_scope") state.status = "ready";
      })
      .addCase(fetchClientCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (state.countriesRequest?.requestId === action.meta.requestId) {
          state.countriesRequest = null;
          // Release the gate so each surface shows its own error state
          // rather than an endless skeleton.
          if (state.status === "resolving_scope") state.status = "ready";
        }
      })
      .addCase(switchWorkspaceAndUpdateCountry.pending, (state, action) => {
        state.loading = true;
        // Enter the switching transition: data-fetch effects gate on
        // status === "ready", so nothing fetches until the new workspace's
        // countries resolve and the country is clamped. filtersSlice
        // clears currentCountry on this same action, so the (new id_client,
        // old country) pair is never a committed, observable state.
        state.status = "switching";
        // Clear old countries immediately so pollers wait for fresh data
        state.countries = [];
        state.defaultCountry = null;
        // Set workspace immediately so that fetchClientCountries can use the new id_client
        const { workspace } = action.meta.arg;
        if (workspace) {
          const nextWorkspaceKey = workspace?.key;
          state.selectedWorkspace = workspace;

          // Recalculate id_client based on selected workspace
          if (state.userData) {
            // Staff switching to an implicit (non-member) workspace: synthesize
            // the entry so the selection sticks and downstream consumers resolve.
            ensureImplicitWorkspaceEntry(state.userData, nextWorkspaceKey);
            state.id_client = extractClientId(state.userData, workspace);
          }
        }
      })
      .addCase(switchWorkspaceAndUpdateCountry.fulfilled, (state) => {
        state.loading = false;
        // Transition complete — the new workspace's country is resolved, so
        // gated fetch effects may run again.
        state.status = "ready";
      })
      .addCase(switchWorkspaceAndUpdateCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Leave the switch resolved so the UI is not stranded behind the gate;
        // isFilterContextConsistent still guards against a missing country.
        state.status = "ready";
      })
      // True impersonation — Firebase identity swapped via signInWithCustomToken.
      // onAuthStateChanged fires after the token swap, which triggers ProtectedRoute
      // to re-fetch user data for the new identity.
      .addCase(impersonateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(impersonateUser.fulfilled, (state, action) => {
        state.loading = false;
        authSlice.caseReducers.setImpersonationActive(state, {
          payload: action.payload,
        });
      })
      .addCase(impersonateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(exitImpersonation.pending, (state) => {
        state.loading = true;
      })
      .addCase(exitImpersonation.fulfilled, (state, action) => {
        state.loading = false;
        authSlice.caseReducers.clearImpersonation(state);
        // Restore the admin's identity in Redux. signInWithCustomToken won't
        // fire onAuthStateChanged (unsubscribed after init), so set it manually.
        if (action.payload?.adminUser) {
          state.user = action.payload.adminUser;
          state.isAuthenticated = true;
        }
      })
      .addCase(exitImpersonation.rejected, (state) => {
        state.loading = false;
        authSlice.caseReducers.clearImpersonation(state);
        // Restore failed → the thunk signed out. Reflect logged-out state so the
        // app routes to login instead of stranding the admin as the target user.
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const {
  setUser,
  setInitialized,
  clearError,
  forceRefreshUserData,
  setImpersonationActive,
  clearImpersonation,
} = authSlice.actions;
export default authSlice.reducer;
